/**
 * Case types for the provider network
 * These types represent the standardized case structure used across partner apps.
 * Conceptually, a case is a conversation between a clinician and a patient.
 */

import {
  ExternalMemberID,
  MDICaseID,
  ProviderNetworkMemberID,
  MDIClinicianID,
  MDIPatientID,
  asMDICaseID,
  asMDIClinicianID,
  asMDIPatientID,
} from "@repo/db/branded-types";

// Re-export the branded types and constructor functions for convenience
export { asMDICaseID, asMDIClinicianID, asMDIPatientID };
export type { MDICaseID, MDIClinicianID, MDIPatientID };

// Simple type aliases for status fields
export type MDIStatusName = string;
export type MDIStatusReason = string;

export const CASE_STATUS = {
  REQUESTED: "requested",
  ACTIVE: "active",
  CANCELLED: "cancelled",
  COMPLETED: "completed",
} as const;

export interface Case {
  id?: string;
  case_id?: MDICaseID;
  patient_id?: MDIPatientID;
  case_status?: {
    name: string;
    reason?: string;
    created_at?: string;
    updated_at?: string;
  };
  created_at?: string;
  updated_at?: string;
  case_assignment?: {
    clinician?: {
      first_name?: string;
      last_name?: string;
      photo?: {
        url?: string;
        url_thumbnail?: string;
      };
      clinician_id?: string;
    };
  };
  patient?: {
    patient_id?: MDIPatientID;
    first_name?: string;
    last_name?: string;
    date_of_birth?: string;
  };
  prioritized?: boolean;
  prioritized_reason?: string;
  prioritized_at?: string;
  has_unread_messages?: boolean;
  last_message?: {
    timestamp: string;
    content: string;
    is_from_clinician: boolean;
  };
}

export interface CaseListData {
  current: Case[];
  past: Case[];
  patients?: Array<{
    providerNetworkMemberId?: ProviderNetworkMemberID | null;
    externalMemberId?: ExternalMemberID | null;
  }>; // Patient data for ID mapping from database
}

export interface PatientCasesResponse {
  data: Case[];
  error?: string;
}

export type CaseStatus = "requested" | "active" | "cancelled" | "completed";

export const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/**
 * Prefer root `updated_at`; fall back to nested status timestamp when present.
 * Returns `undefined` for missing or malformed dates.
 */
export function getCaseUpdatedAtTimestamp(caseItem: Case): number | undefined {
  const raw = caseItem.updated_at ?? caseItem.case_status?.updated_at;
  if (!raw) return undefined;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? undefined : ms;
}

/**
 * Comparator: most recent first by `updated_at`. Cases with no parseable
 * timestamp sort to the end so they remain visible but don't claim the top
 * of the list.
 */
export function compareCasesMostRecentFirst(a: Case, b: Case): number {
  const aMs = getCaseUpdatedAtTimestamp(a);
  const bMs = getCaseUpdatedAtTimestamp(b);
  if (aMs === undefined && bMs === undefined) return 0;
  if (aMs === undefined) return 1;
  if (bMs === undefined) return -1;
  return bMs - aMs;
}

/**
 * When status is completed or cancelled, the case remains "current" until
 * 24 hours after `updated_at` (the time of the terminal transition).
 */
export function isWithinTerminalGracePeriod(
  caseItem: Case,
  nowMs: number
): boolean {
  const updatedMs = getCaseUpdatedAtTimestamp(caseItem);
  if (updatedMs === undefined) return false;
  return nowMs - updatedMs < TWENTY_FOUR_HOURS_MS;
}

export interface CaseListError {
  message: string;
  code?: string;
  statusCode?: number;
}

export type UnreadMessageInfo = {
  hasUnread: boolean;
  messageType?: "clinician" | "support";
};

export type StaffMessage = {
  text: string;
  read_message_info: string | null | undefined;
  created_at: string;
};

export interface MessagingSessionResult {
  chatUrl: string | null;
  /**
   * Only present when the provider returns one. It feeds the OTP auto-fill in
   * the messaging iframe, which no-ops without it — so absence degrades the
   * session at worst, it does not break it.
   */
  verificationCode?: string;
  caseId: string | null;
}
